import { RTC_PROVIDERS } from '../providerIds'
import { normalizeResolvedVideoCallQuality } from '../routing/videoCallQualityContract'

export function buildLocalPeerMetadata(localParticipantIdentity) {
  if (!localParticipantIdentity?.participantId) {
    return null
  }

  return {
    participantId: localParticipantIdentity.participantId,
    identityKey: localParticipantIdentity.participantId,
    userId: localParticipantIdentity.userId || null,
    actorType: localParticipantIdentity.actorType || null,
    actorId: localParticipantIdentity.actorId || null,
    displayName: localParticipantIdentity.displayName || null,
    profilePicId: localParticipantIdentity.profilePicId || null,
    role: localParticipantIdentity.role || null,
  }
}

// Provider payload metadata carries session context (bookedEventId, flowType)
// but not the identity contract fields remote rosters resolve participants
// with, so overlay the richer local identity instead of discarding it.
export function mergeProviderPeerMetadata(payloadPeerMetadata, localPeerMetadata) {
  if (!localPeerMetadata) {
    return payloadPeerMetadata || null
  }

  if (!payloadPeerMetadata) {
    return localPeerMetadata
  }

  let parsedPayloadMetadata = payloadPeerMetadata
  if (typeof payloadPeerMetadata === 'string') {
    try {
      parsedPayloadMetadata = JSON.parse(payloadPeerMetadata)
    } catch (error) {
      return localPeerMetadata
    }
  }

  if (
    !parsedPayloadMetadata ||
    typeof parsedPayloadMetadata !== 'object' ||
    Array.isArray(parsedPayloadMetadata)
  ) {
    return localPeerMetadata
  }

  const definedLocalMetadata = Object.entries(localPeerMetadata).reduce(
    (result, [key, value]) => {
      if (value !== null && value !== undefined) {
        result[key] = value
      }

      return result
    },
    {},
  )

  return {
    ...parsedPayloadMetadata,
    ...definedLocalMetadata,
  }
}

export function buildHundredMsRtcProps(
  sessionRoutingDecision,
  agoraRtcProps,
  localParticipantIdentity,
) {
  const hundredMsPayload = sessionRoutingDecision?.providerPayloads?.[RTC_PROVIDERS.hundredMs]
  const resolvedVideoQuality = normalizeResolvedVideoCallQuality(
    hundredMsPayload?.videoQuality ||
      sessionRoutingDecision?.videoQuality ||
      agoraRtcProps?.videoQuality,
  )
  if (!hundredMsPayload?.authToken || !hundredMsPayload?.userName) {
    return null
  }

  return {
    authToken: hundredMsPayload.authToken,
    userName: hundredMsPayload.userName,
    role: hundredMsPayload.role,
    roomId: hundredMsPayload.roomId,
    roomName: hundredMsPayload.roomName,
    userId: hundredMsPayload.userId,
    peerMetadata: mergeProviderPeerMetadata(
      hundredMsPayload.peerMetadata,
      buildLocalPeerMetadata(localParticipantIdentity),
    ),
    rememberDeviceSelection: hundredMsPayload.rememberDeviceSelection,
    autoManageVideo: hundredMsPayload.autoManageVideo,
    autoManageWakeLock: hundredMsPayload.autoManageWakeLock,
    // viewerOnly payloads (audit observers) never publish, regardless of what
    // the hosting surface passes.
    enableAudio: hundredMsPayload.viewerOnly === true ? false : agoraRtcProps?.enableAudio,
    enableVideo: hundredMsPayload.viewerOnly === true ? false : agoraRtcProps?.enableVideo,
    // enableScreensharing is the DESIRED local screenshare state; the
    // capabilities.screenshare flag only says the feature is allowed.
    enableScreensharing: hundredMsPayload.enableScreensharing,
    screenshareAllowed:
      hundredMsPayload.viewerOnly === true
        ? false
        : hundredMsPayload.screenshareAllowed ??
          sessionRoutingDecision?.capabilities?.screenshare === true,
    cameraDeviceId: agoraRtcProps?.cameraDeviceId,
    microphoneDeviceId: agoraRtcProps?.microphoneDeviceId,
    playbackDeviceId: agoraRtcProps?.playbackDeviceId,
    layout: agoraRtcProps?.layout,
    videoQuality: resolvedVideoQuality,
  }
}

export function buildDailyRtcProps(
  sessionRoutingDecision,
  agoraRtcProps,
  localParticipantIdentity,
) {
  const dailyPayload = sessionRoutingDecision?.providerPayloads?.[RTC_PROVIDERS.dailyCo]
  const resolvedVideoQuality = normalizeResolvedVideoCallQuality(
    dailyPayload?.videoQuality ||
      sessionRoutingDecision?.videoQuality ||
      agoraRtcProps?.videoQuality,
  )
  if (!dailyPayload?.roomUrl) {
    return null
  }

  return {
    roomUrl: dailyPayload.roomUrl,
    token: dailyPayload.token,
    userName:
      dailyPayload.userName ||
      localParticipantIdentity?.displayName ||
      'Daily participant',
    userId: dailyPayload.userId || localParticipantIdentity?.userId || null,
    participantMetadata: mergeProviderPeerMetadata(
      dailyPayload.participantMetadata,
      buildLocalPeerMetadata(localParticipantIdentity),
    ),
    fixtureParticipants: Array.isArray(dailyPayload.fixtureParticipants)
      ? dailyPayload.fixtureParticipants
      : undefined,
    initialSessionState: dailyPayload.initialSessionState,
    enableAudio: dailyPayload.viewerOnly === true ? false : agoraRtcProps?.enableAudio,
    enableVideo: dailyPayload.viewerOnly === true ? false : agoraRtcProps?.enableVideo,
    enableScreensharing: dailyPayload.enableScreensharing,
    screenshareAllowed:
      dailyPayload.viewerOnly === true
        ? false
        : dailyPayload.screenshareAllowed ??
          sessionRoutingDecision?.capabilities?.screenshare === true,
    cameraDeviceId: agoraRtcProps?.cameraDeviceId,
    microphoneDeviceId: agoraRtcProps?.microphoneDeviceId,
    playbackDeviceId: agoraRtcProps?.playbackDeviceId,
    layout: agoraRtcProps?.layout,
    videoQuality: resolvedVideoQuality,
  }
}

export function buildZoomRtcProps(
  sessionRoutingDecision,
  agoraRtcProps,
  localParticipantIdentity,
) {
  const zoomPayload = sessionRoutingDecision?.providerPayloads?.[RTC_PROVIDERS.zoomVideoSdk]
  const resolvedVideoQuality = normalizeResolvedVideoCallQuality(
    zoomPayload?.videoQuality ||
      sessionRoutingDecision?.videoQuality ||
      agoraRtcProps?.videoQuality,
  )
  if (!zoomPayload?.sessionName || !zoomPayload?.token) {
    return null
  }

  return {
    sessionName: zoomPayload.sessionName,
    sessionPassword: zoomPayload.sessionPassword,
    token: zoomPayload.token,
    userName:
      zoomPayload.userName ||
      localParticipantIdentity?.displayName ||
      'AUMHUM participant',
    userId: zoomPayload.userId || localParticipantIdentity?.userId || null,
    participantMetadata: mergeProviderPeerMetadata(
      zoomPayload.participantMetadata,
      buildLocalPeerMetadata(localParticipantIdentity),
    ),
    language: zoomPayload.language,
    dependentAssets: zoomPayload.dependentAssets,
    webEndpoint: zoomPayload.webEndpoint,
    sessionIdleTimeoutMins: zoomPayload.sessionIdleTimeoutMins,
    enableAudio: zoomPayload.viewerOnly === true ? false : agoraRtcProps?.enableAudio,
    enableVideo: zoomPayload.viewerOnly === true ? false : agoraRtcProps?.enableVideo,
    enableScreensharing: zoomPayload.enableScreensharing,
    screenshareAllowed:
      zoomPayload.viewerOnly === true
        ? false
        : zoomPayload.screenshareAllowed ??
          sessionRoutingDecision?.capabilities?.screenshare === true,
    cameraDeviceId: agoraRtcProps?.cameraDeviceId,
    microphoneDeviceId: agoraRtcProps?.microphoneDeviceId,
    playbackDeviceId: agoraRtcProps?.playbackDeviceId,
    layout: agoraRtcProps?.layout,
    videoQuality: resolvedVideoQuality,
  }
}

export function getParticipantTransportKey(participant, providerId) {
  if (!participant) {
    return null
  }

  if (providerId === RTC_PROVIDERS.hundredMs) {
    return participant?.transport?.peerId || null
  }

  if (providerId === RTC_PROVIDERS.dailyCo) {
    return (
      participant?.transport?.sessionId ??
      participant?.transport?.dailySessionId ??
      null
    )
  }

  if (providerId === RTC_PROVIDERS.zoomVideoSdk) {
    return (
      participant?.transport?.userId ??
      participant?.transport?.zoomUserId ??
      null
    )
  }

  return participant?.transport?.uid ?? participant?.transport?.rtcUid ?? null
}
